import { useMemo } from "react";
import { motion } from "framer-motion";
import {
  Flame, Award, BookOpen, Target, Trophy, Zap, Rocket, Crown, Sparkles,
  GraduationCap, Star,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Emails that own legendary achievements. Kept in sync with CREATOR_EMAILS on
// the backend (server.py) so the Phoenix badge / mascot lives only on this account.
const CREATOR_EMAILS = new Set(["admin@lectura.app"]);
const isCreatorEmail = (email) => CREATOR_EMAILS.has((email || "").toLowerCase());

/**
 * Achievement badges — computed client-side from the existing dashboard payload
 * so we don't need a new endpoint. Each badge tier unlocks at a milestone and
 * shows both the current progress AND the next milestone as a soft nudge.
 *
 * Props: `dashboard` = the object returned from GET /api/me/dashboard
 */
const clamp = (n) => Math.max(0, Math.min(1, n));

const tierBadges = ({ current_streak = 0, longest_streak = 0, total_lectures = 0,
                     quizzes_taken = 0, avg_score = 0, concepts_mastered = 0,
                     concepts_total = 0 }, { isCreator = false } = {}) => {
  const bestStreak = Math.max(current_streak, longest_streak);
  const masteredPct = concepts_total ? (concepts_mastered / concepts_total) * 100 : 0;

  const streakTiers = [
    // Streak tiers
    {
      id: "spark", tier: "Streak", icon: Flame, name: "Spark",
      desc: "Study 3 days in a row.",
      unlocked: bestStreak >= 3, progress: clamp(bestStreak / 3), goal: 3, at: bestStreak,
      color: "text-amber-500", ring: "ring-amber-500/40",
    },
    {
      id: "blaze", tier: "Streak", icon: Flame, name: "Blaze",
      desc: "A whole week of daily study.",
      unlocked: bestStreak >= 7, progress: clamp(bestStreak / 7), goal: 7, at: bestStreak,
      color: "text-orange-500", ring: "ring-orange-500/40",
    },
    {
      id: "wildfire", tier: "Streak", icon: Flame, name: "Wildfire",
      desc: "30-day study streak.",
      unlocked: bestStreak >= 30, progress: clamp(bestStreak / 30), goal: 30, at: bestStreak,
      color: "text-red-500", ring: "ring-red-500/40",
    },
  ];
  // Phoenix is a legendary badge reserved for the app creator — hidden entirely
  // for every other account so it feels genuinely exclusive.
  if (isCreator) {
    streakTiers.push({
      id: "phoenix", tier: "Streak", icon: Crown, name: "Phoenix",
      desc: "100-day study streak. Legendary — creator-only.",
      unlocked: bestStreak >= 100, progress: clamp(bestStreak / 100), goal: 100, at: bestStreak,
      color: "text-fuchsia-500", ring: "ring-fuchsia-500/40",
      legendary: true,
    });
  }

  return [
    ...streakTiers,
    // Creation tiers
    {
      id: "first-lecture", tier: "Creator", icon: BookOpen, name: "First Draft",
      desc: "Created your first lecture.",
      unlocked: total_lectures >= 1, progress: clamp(total_lectures / 1), goal: 1, at: total_lectures,
      color: "text-emerald-500", ring: "ring-emerald-500/40",
    },
    {
      id: "bookworm", tier: "Creator", icon: BookOpen, name: "Bookworm",
      desc: "Created 10 lectures.",
      unlocked: total_lectures >= 10, progress: clamp(total_lectures / 10), goal: 10, at: total_lectures,
      color: "text-teal-500", ring: "ring-teal-500/40",
    },
    {
      id: "scholar", tier: "Creator", icon: GraduationCap, name: "Scholar",
      desc: "Created 50 lectures.",
      unlocked: total_lectures >= 50, progress: clamp(total_lectures / 50), goal: 50, at: total_lectures,
      color: "text-indigo-500", ring: "ring-indigo-500/40",
    },
    {
      id: "polymath", tier: "Creator", icon: Rocket, name: "Polymath",
      desc: "Created 100 lectures.",
      unlocked: total_lectures >= 100, progress: clamp(total_lectures / 100), goal: 100, at: total_lectures,
      color: "text-violet-500", ring: "ring-violet-500/40",
    },
    // Quiz tiers
    {
      id: "quiz-taker", tier: "Quiz", icon: Trophy, name: "Contender",
      desc: "Took 5 quizzes.",
      unlocked: quizzes_taken >= 5, progress: clamp(quizzes_taken / 5), goal: 5, at: quizzes_taken,
      color: "text-sky-500", ring: "ring-sky-500/40",
    },
    {
      id: "quiz-master", tier: "Quiz", icon: Trophy, name: "Quiz Master",
      desc: "Took 25 quizzes.",
      unlocked: quizzes_taken >= 25, progress: clamp(quizzes_taken / 25), goal: 25, at: quizzes_taken,
      color: "text-blue-500", ring: "ring-blue-500/40",
    },
    {
      id: "sharp-shooter", tier: "Quiz", icon: Zap, name: "Sharp Shooter",
      desc: "Average quiz score of 80% or higher.",
      unlocked: avg_score >= 80 && quizzes_taken >= 3,
      progress: clamp((avg_score || 0) / 80), goal: 80, at: avg_score,
      color: "text-yellow-500", ring: "ring-yellow-500/40",
    },
    // Mastery tier
    {
      id: "conceptualist", tier: "Mastery", icon: Target, name: "Conceptualist",
      desc: "Master 10 concepts across your lectures.",
      unlocked: concepts_mastered >= 10, progress: clamp(concepts_mastered / 10),
      goal: 10, at: concepts_mastered,
      color: "text-pink-500", ring: "ring-pink-500/40",
    },
    {
      id: "walking-encyclopedia", tier: "Mastery", icon: Star, name: "Walking Encyclopedia",
      desc: "Master 80% of your concepts.",
      unlocked: masteredPct >= 80 && concepts_total >= 5,
      progress: clamp((masteredPct || 0) / 80), goal: 80,
      at: Math.round(masteredPct),
      color: "text-lime-500", ring: "ring-lime-500/40",
    },
  ];
};

const BadgeTile = ({ b }) => {
  const Icon = b.icon;
  const suffix = b.id === "sharp-shooter" || b.id === "walking-encyclopedia" ? "%" : "";
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      data-testid={`badge-tile-${b.id}`}
      className={`relative rounded-2xl border p-4 text-center transition ${
        b.unlocked
          ? "border-border bg-card shadow-sm"
          : "border-dashed border-border bg-secondary/30 opacity-70"
      }`}
    >
      <div
        className={`mx-auto grid place-items-center h-12 w-12 rounded-2xl ring-2 ${
          b.unlocked ? `${b.ring} bg-background` : "ring-border bg-secondary"
        }`}
      >
        <Icon className={`h-6 w-6 ${b.unlocked ? b.color : "text-muted-foreground/50"}`} />
      </div>
      <p className="mt-2.5 font-serif text-sm font-bold leading-tight">{b.name}</p>
      <p className="mt-0.5 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
        {b.tier}
      </p>
      <p className="mt-1.5 text-[11px] text-muted-foreground leading-snug min-h-[2.5rem]">
        {b.desc}
      </p>
      <div className="mt-2 h-1.5 rounded-full bg-secondary overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${Math.round(b.progress * 100)}%` }}
          transition={{ duration: 0.8 }}
          className={`h-full rounded-full ${b.unlocked ? "bg-accent" : "bg-muted-foreground/40"}`}
        />
      </div>
      <p className="mt-1 text-[10px] font-mono tabular-nums text-muted-foreground">
        {Math.min(b.at || 0, b.goal)}{suffix} / {b.goal}{suffix}
      </p>
      {b.unlocked && (
        <span className="absolute -top-2 -right-2 rounded-full bg-accent text-accent-foreground text-[9px] font-bold px-2 py-0.5 shadow-sm">
          {b.legendary ? "LEGENDARY" : "UNLOCKED"}
        </span>
      )}
      {b.legendary && !b.unlocked && (
        <span className="absolute -top-2 -right-2 rounded-full bg-fuchsia-600 text-white text-[9px] font-bold px-2 py-0.5 shadow-sm">
          CREATOR
        </span>
      )}
    </motion.div>
  );
};

export const BadgesCard = ({ dashboard }) => {
  const { user } = useAuth();
  const isCreator = isCreatorEmail(user?.email);
  const badges = useMemo(() => tierBadges(dashboard || {}, { isCreator }), [dashboard, isCreator]);
  const unlocked = badges.filter((b) => b.unlocked).length;

  return (
    <div className="rounded-2xl border border-border bg-card p-6 shadow-sm" data-testid="badges-card">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            Achievements
          </span>
          <h3 className="mt-1 font-serif text-2xl font-bold flex items-center gap-2">
            <Award className="h-6 w-6 text-accent" /> Study badges
          </h3>
        </div>
        <div className="text-right">
          <p data-testid="badges-unlocked-count" className="font-serif text-2xl font-bold">
            {unlocked}
            <span className="text-base text-muted-foreground font-sans"> / {badges.length}</span>
          </p>
          <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Unlocked</p>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {badges.map((b) => (
          <BadgeTile key={b.id} b={b} />
        ))}
      </div>
      <div className="mt-5 flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <Sparkles className="h-3.5 w-3.5 text-accent" />
        <span>Complete a quiz or create a new lecture to level up your badges.</span>
      </div>
    </div>
  );
};
